import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MappedReportService, MappedReportInstanceDetail } from '../../../core/mapped-reports/mapped-report.service';
import { ReportService } from '../../../../report/report.service';

const SPLIT_RATIO_KEY = 'mapped-report.viewer.split-ratio';
// Applied to <body> while the viewer is active so global styles can hide the
// projects-wrapper's empty 310px map band (the viewer owns its own map pane).
const VIEWER_BODY_CLASS = 'mr-viewer-route';

@Component({
  selector: 'app-mapped-report-viewer-page',
  templateUrl: './mapped-report-viewer-page.component.html',
  styleUrls: ['./mapped-report-viewer-page.component.scss'],
  standalone: false,
})
export class MappedReportViewerPageComponent implements OnInit, OnDestroy {
  projectId: string;
  dailyReportId: string;
  groupKey: string;
  reportId: string | null = null;
  detail: MappedReportInstanceDetail | null = null;
  loading = true;
  loadError: string | null = null;

  downloading = false;
  downloadError = false;

  // Rendered form height (px), reported by the PDF pane. Drives both panes' height
  // so the ~square form fills its pane with no dead space and the map matches it.
  formHeight: number | null = null;

  splitRatio = this.readSplitRatio();

  private destroy$ = new Subject<void>();
  private destroyed = false;
  private dragMove: ((ev: MouseEvent) => void) | null = null;
  private dragUp: (() => void) | null = null;

  constructor(
    private route: ActivatedRoute,
    private svc: MappedReportService,
    private rpt: ReportService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit() {
    this.renderer.addClass(this.document.body, VIEWER_BODY_CLASS);
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(async pm => {
      this.projectId = pm.get('projectId') as string;
      this.dailyReportId = pm.get('dailyReportId') as string;
      this.groupKey = pm.get('groupKey') as string;
      await this.load();
    });
  }

  // The PDF pane measured the rendered form's height; clamp it to a sane band so
  // a stray 0 or an unusually long form can't collapse/blow out the layout, then
  // size both panes to it.
  onFormHeight(height: number) {
    if (!height || height < 1) return;
    this.formHeight = Math.min(Math.max(height, 480), 1100);
  }

  onSplitChange(ratio: number) {
    this.splitRatio = ratio;
    try {
      localStorage.setItem(SPLIT_RATIO_KEY, String(ratio));
    } catch {
      /* localStorage may be unavailable */
    }
  }

  onSplitterDown(e: MouseEvent) {
    if (this.dragMove) return;
    e.preventDefault();
    this.dragMove = (ev: MouseEvent) => {
      const ratio = Math.min(0.7, Math.max(0.3, ev.clientX / window.innerWidth));
      this.splitRatio = ratio;
    };
    this.dragUp = () => {
      this.teardownDrag();
      this.onSplitChange(this.splitRatio);
    };
    window.addEventListener('mousemove', this.dragMove);
    window.addEventListener('mouseup', this.dragUp);
  }

  // Generate the PDF on demand via the backend, then trigger a browser download.
  // In-flight guard keeps double-clicks from generating twice.
  async download() {
    if (this.downloading || !this.reportId || !this.detail?.html) {
      return;
    }
    this.downloading = true;
    this.downloadError = false;
    let objectUrl: string | null = null;
    try {
      const { blob, filename } = await this.rpt.generatePdfFromHtml(this.reportId, this.detail.html, {
        projectId: this.projectId,
        form_number: this.detail.formNumber,
        date: this.detail.reportDate,
      });
      objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename || 'report.pdf';
      this.document.body.appendChild(anchor);
      anchor.click();
      this.document.body.removeChild(anchor);
    } catch (e) {
      console.error('Failed to generate mapped report PDF', e);
      this.downloadError = true;
    } finally {
      if (objectUrl) {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch {
          /* nothing to clean up */
        }
      }
      this.downloading = false;
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.renderer.removeClass(this.document.body, VIEWER_BODY_CLASS);
    this.teardownDrag();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private teardownDrag() {
    if (this.dragMove) window.removeEventListener('mousemove', this.dragMove);
    if (this.dragUp) window.removeEventListener('mouseup', this.dragUp);
    this.dragMove = null;
    this.dragUp = null;
  }

  private async load() {
    this.loading = true;
    this.loadError = null;
    try {
      const templates = await this.svc.getMappedReportTemplates(this.projectId);
      if (this.destroyed) return;
      const reportId = templates[0]?.id;
      if (!reportId) {
        this.loadError = 'No mapped report template configured for this project.';
        return;
      }
      this.reportId = reportId;
      const detail = await this.svc.getMappedReportInstanceDetail(this.dailyReportId, this.groupKey, reportId);
      if (this.destroyed) return;
      this.detail = detail;
    } catch (e: any) {
      if (this.destroyed) return;
      this.loadError = e?.status === 404 ? 'Form not found.' : 'Could not load form.';
      console.error('Failed to load mapped report viewer detail', e);
    } finally {
      if (!this.destroyed) this.loading = false;
    }
  }

  private readSplitRatio(): number {
    try {
      const v = parseFloat(localStorage.getItem(SPLIT_RATIO_KEY) || '0.5');
      return Number.isFinite(v) && v >= 0.3 && v <= 0.7 ? v : 0.5;
    } catch {
      return 0.5;
    }
  }
}
